import React, { ErrorInfo, PureComponent } from "react";
import { Accordion, Button, TitleHeader } from "@xapp/components";
import { reportStudioError } from "utils/clientErrorReporting";
import style from "./style.scss";

export interface ErrorBoundaryStateProps { }
export interface ErrorBoundaryOwnProps {
    children?: JSX.Element[] | JSX.Element;
}

export type ErrorBoundaryProps = ErrorBoundaryStateProps & ErrorBoundaryOwnProps;

export interface ErrorBoundaryState {
    error?: Error;
    info?: ErrorInfo;
}

/**
 * A copy of the error whose stack carries React's component stack after the browser's, so the
 * report says which component tree threw without changing the error the page shows.
 */
export function withComponentStack(error: Error, info: ErrorInfo): Error {
    const componentStack = info?.componentStack?.trim();
    if (!componentStack) {
        return error;
    }
    const copy = new Error(error.message);
    copy.name = error.name;
    copy.stack = `${error.stack || `${error.name}: ${error.message}`}\n\nComponent stack:\n${componentStack}`;
    return copy;
}

export const DOCUMENTATION_URL = window.xaStudioConfig?.documentationUrl || "https://documentation.xapp.ai";

export class ErrorBoundaryView extends PureComponent<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);

        this.state = {};

        this.handleErrorEvent = this.handleErrorEvent.bind(this);
        this.handleRefreshClick = this.handleRefreshClick.bind(this);
    }

    static getDerivedStateFromError(error: Error) {
        return { error };
    }

    componentDidMount() {
        window.addEventListener("error", this.handleErrorEvent);
    }

    componentWillUnmount() {
        window.removeEventListener("error", this.handleErrorEvent);
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        // Report to our own events index (STUDIO_ERROR), with the component stack appended so
        // the signature names the component tree that threw
        reportStudioError(withComponentStack(error, info), "boundary");

        this.setState({
            error,
            info
        });
    }

    /**
     * prevents the error from bubbling up to the virtual console in tests
     * @param e
     */
    handleErrorEvent(e: Event) {
        if (__ENV__ === "test") {
            e.preventDefault();
        }
    }

    handleRefreshClick() {
        window.location.reload();
    }

    render() {
        const { error } = this.state;

        if (error) {
            return this.renderError();
        }

        return this.props.children;
    }

    renderDevError() {
        const { error } = this.state;

        return (
            <>
                <Accordion title="Show Error Details">
                    <h2>Error</h2>
                    <pre className={style.pre}>{error.toString()}</pre>
                    <pre className={style.pre} dangerouslySetInnerHTML={this.renderComponentStack()} />
                </Accordion>
            </>
        );
    }

    renderError() {
        return (
            <section className={style.wrap}>
                <TitleHeader static value="Oh no! Something's not right here." />

                {this.renderProdError()}
                {this.renderDevError()}
            </section>
        );
    }

    renderComponentStack() {
        if (this.state.info) {
            return {
                __html: this.state.info.componentStack.trim()
            };
        }
    }

    renderProdError() {
        return (
            <>
                <p>
                    There's a good chance that this was a one-time thing. Please{" "}
                    <button onClick={this.handleRefreshClick}>
                        refresh the page
                    </button>{" "}
                    and try again.
                </p>

                <p>
                    If that's not the case, and you are shown this screen over and over, fear not: we are here to help
                </p>

                <footer className={style.cta}>
                    <a target="_blank" rel="noopener noreferrer" href={DOCUMENTATION_URL}>
                        <Button variation="primary" label="Take me to Support" />
                    </a>
                </footer>
            </>
        );
    }
}
